// -----------------------------------------------------------------------------
// PDF rules (Part 5, boards A3.1 / A3.1.1 / P0–P3) — pure functions, unit-tested in tests/unit/pdf.test.ts.
//
// How a PDF is made (browser print box, Kyle 11/Oct/26):
//   A3.1 Export PDF (ExportPdf.tsx) → A3.1.1 Download PDFs → Download = rpc issues_publish (takes the version,
//   returns PdfData) → printer.tsx renders PrintPages.tsx into a hidden print area → window.print().
//   The file name the print box suggests = document.title = pdfFileName() / masterFileName().
// Page sizes: every page A4 portrait 210 × 297 mm (cover + signing page too since round 22, 17/Oct/26); styles.css "PRINT".
// -----------------------------------------------------------------------------
import type { IncludeIssues, PdfIssue } from "../../../core/api/types";

/** Up to 35 issues per component summary page (P2; round 24 #8 / #14: 35 rows of 24 px fit above the footer). */
export const SUMMARY_ROWS = 35;

/** Next version a Download will take: none yet → V00, latest V02 → V03. */
export function nextVersion(latest: string | null | undefined): string {
  if (!latest) return "V00";
  const n = Number(latest.replace(/^V/, ""));
  return `V${String((Number.isFinite(n) ? n : -1) + 1).padStart(2, "0")}`;
}

/** File name of a component PDF (v0.7.1, v070-005): "2026-09-26 BIM-024 LZ-Low zone level 14–43-V02" — report date (ISO, so
 *  files sort by date), project code (v0.9.1, round 32 #11 — in the FILE NAME only; it is still never printed on a page),
 *  prefix, name, version. Characters Windows refuses become "-". */
export function pdfFileName(prefix: string, name: string, version: string, reportDate: string, projectCode = ""): string {
  return safeName(`${reportDate} ${projectCode ? projectCode + " " : ""}${prefix}-${name}-${version}`);
}

/** File name of the master summary (v0.7.1): "2026-09-26 BIM-024 Master summary". */
export function masterFileName(reportDate: string, projectCode = ""): string {
  return safeName(`${reportDate} ${projectCode ? projectCode + " " : ""}Master summary`);
}

const safeName = (s: string) => s.replace(/[\\/:*?"<>|]/g, "-").trim();

/** Issue pages (P3) of a component PDF: Issues to include (open = Open + Critical) and the ticked disciplines. Voided never. */
export function issuePages(issues: PdfIssue[], include: { issues: IncludeIssues; disciplines: string[] }): PdfIssue[] {
  const statuses = include.issues === "critical" ? ["Critical"] : include.issues === "open" ? ["Open", "Critical"] : ["Open", "Critical", "Closed"];
  return issues.filter((i) => statuses.includes(i.status) && include.disciplines.includes(i.discipline));
}

/** Split rows into pages of `per` (P2 component summary). Always at least one page. */
export function chunk<T>(rows: T[], per = SUMMARY_ROWS): T[][] {
  const out: T[][] = [];
  for (let k = 0; k < rows.length; k += per) out.push(rows.slice(k, k + per));
  return out.length ? out : [[]];
}

/**
 * Split an issue's resolution log over pages (P3 "Continues on next page →").
 *   heights = measured height (px) of each entry, in print order (newest first)
 *   first   = room left for the log on the issue's own page; next = room on a continuation page
 * Returns the number of entries on each page; an entry taller than a whole page still gets a page of its own.
 */
export function splitLog(heights: number[], first: number, next: number): number[] {
  const pages: number[] = [];
  let room = first, n = 0;
  for (const h of heights) {
    if (h > room && n > 0) { pages.push(n); n = 0; room = next; }
    else if (h > room && n === 0 && pages.length === 0 && room < next) { pages.push(0); room = next; }
    n += 1; room -= h;
  }
  pages.push(n);
  return pages;
}

/** Open counts per discipline (P2 chips) and the total open (Open + Critical). */
export function openCounts(issues: PdfIssue[], codes: string[]): { total: number; by: Record<string, number> } {
  const by: Record<string, number> = Object.fromEntries(codes.map((c) => [c, 0]));
  let total = 0;
  for (const i of issues) if (i.status === "Open" || i.status === "Critical") { total += 1; by[i.discipline] = (by[i.discipline] ?? 0) + 1; }
  return { total, by };
}

/** Component titles on the cover: "LZ · Low zone level 14–43". */
export const pdfTitle = (prefix: string, name: string): string => `${prefix} · ${name}`;
